// frontend/src/components/ResetPassword.js
import React, { useMemo, useState } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  TextField,
  Typography,
  Alert,
  Stack,
  CircularProgress,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { useSearchParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import { authAPI } from '../services/api';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';

const normalizeToken = (v) => String(v || '').trim();

export default function ResetPassword() {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = useMemo(() => normalizeToken(params.get('token')), [params]);

  const [newPassword, setNewPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [info, setInfo] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const [show1, setShow1] = useState(false);
  const [show2, setShow2] = useState(false);

  const minLenOk = newPassword.length >= 6;
  const matchOk = newPassword === confirm && confirm.length > 0;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setInfo('');
    setError('');

    if (!token) {
      setError('Reset link is invalid or missing.');
      return;
    }

    if (!newPassword || !confirm) {
      setError('Please enter and confirm your new password.');
      return;
    }

    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirm) {
      setError('Passwords do not match.');
      return;
    }

    setBusy(true);
    try {
      const { data } = await authAPI.resetPassword({ token, newPassword });
      setInfo(data?.message || 'Password reset successfully. Redirecting to login…');
      setNewPassword('');
      setConfirm('');

      // quick redirect
      setTimeout(() => navigate('/login'), 1200);
    } catch (err) {
      const msg = err?.response?.data?.message || 'Could not reset password. Please try again.';
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  if (!token) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'grid',
          placeItems: 'center',
          bgcolor: 'background.default',
          p: 2,
        }}
      >
        <Card sx={{ width: '100%', maxWidth: 520, borderRadius: 3, boxShadow: 3 }}>
          <CardContent sx={{ p: { xs: 2.25, sm: 3 } }}>
            <Stack spacing={1}>
              <Typography variant="h5" sx={{ fontWeight: 900 }}>
                Reset link is invalid
              </Typography>
              <Typography variant="body2" color="text.secondary">
                The reset token is missing or expired. Please request a new reset link.
              </Typography>

              <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1 }}>
                <Button
                  component={RouterLink}
                  to="/forgot-password"
                  variant="contained"
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                >
                  Request new reset link
                </Button>
                <Button
                  component={RouterLink}
                  to="/login"
                  variant="outlined"
                  sx={{ borderRadius: 2, textTransform: 'none', fontWeight: 900 }}
                >
                  Back to login
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        bgcolor: 'background.default',
        px: 2,
        py: 3,
      }}
    >
      <Card sx={{ width: '100%', maxWidth: 460, borderRadius: 3, boxShadow: 3 }}>
        <CardContent sx={{ p: { xs: 2.25, sm: 3 } }}>
          <Stack spacing={1.25}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 900, lineHeight: 1.15 }}>
                Set a new password
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Choose a strong password and confirm it below.
              </Typography>
            </Box>

            {info ? <Alert severity="success">{info}</Alert> : null}
            {error ? <Alert severity="error">{error}</Alert> : null}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <TextField
                fullWidth
                type={show1 ? 'text' : 'password'}
                label="New password"
                margin="normal"
                required
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                disabled={busy}
                helperText={minLenOk ? ' ' : 'Minimum 6 characters'}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShow1((v) => !v)}
                        disabled={busy}
                        aria-label="toggle password visibility"
                      >
                        {show1 ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <TextField
                fullWidth
                type={show2 ? 'text' : 'password'}
                label="Confirm new password"
                margin="normal"
                required
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                disabled={busy}
                helperText={confirm.length ? (matchOk ? 'Passwords match' : 'Passwords do not match') : ' '}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        edge="end"
                        onClick={() => setShow2((v) => !v)}
                        disabled={busy}
                        aria-label="toggle confirm password visibility"
                      >
                        {show2 ? <VisibilityOffIcon /> : <VisibilityIcon />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />

              <Button
                type="submit"
                variant="contained"
                fullWidth
                disabled={busy || !minLenOk || !matchOk}
                sx={{
                  mt: 1.5,
                  borderRadius: 2,
                  py: 1.2,
                  fontWeight: 900,
                  textTransform: 'none',
                }}
              >
                {busy ? (
                  <Stack direction="row" spacing={1} alignItems="center">
                    <CircularProgress size={18} />
                    <span>Resetting…</span>
                  </Stack>
                ) : (
                  'Reset password'
                )}
              </Button>

              <Button
                component={RouterLink}
                to="/login"
                variant="text"
                fullWidth
                disabled={busy}
                sx={{ mt: 1, borderRadius: 2, textTransform: 'none', fontWeight: 800 }}
              >
                Back to login
              </Button>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
}
